import { deferred } from '@thinkalpha/ui-core/deferred';
import { injectable, inject } from 'inversify';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class TradingSocketProvider {
    #worker: Worker | SharedWorker;
    #log: ReactBindings['Logger'];
    #workerReady = deferred<void>();

    constructor(@inject('Logger') private logger: ReactBindings['Logger']) {
        this.#log = logger.getSubLogger({ name: 'TradingSocketProvider' }, { component: 'trading' });

        if (typeof SharedWorker !== 'undefined') {
            if (TRACE) {
                this.#log.trace('SharedWorker available, creating connection');
            }
            this.#worker = new SharedWorker(new URL('./worker.ts', import.meta.url), {
                type: 'module',
                name: 'trading-socket-worker',
            });
            this.#workerReady.resolve();
        } else {
            if (TRACE) {
                this.#log.trace('SharedWorker not available, using singleton Worker');
            }
            this.#worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

            // Wait for the worker to be ready
            const readyHandler = (_event: MessageEvent) => {
                this.#workerReady.resolve();
                (this.#worker as Worker).removeEventListener('message', readyHandler);
            };
            (this.#worker as Worker).addEventListener('message', readyHandler);
        }
    }

    async getWorkerPort(): Promise<MessagePort> {
        await this.#workerReady.promise;

        if (this.#worker instanceof SharedWorker) {
            return this.#worker.port;
        } else {
            // Dedicated Worker
            const channel = new MessageChannel();
            (this.#worker as Worker).postMessage({ type: 'new-client', port: channel.port2 }, [channel.port2]);
            return channel.port1;
        }
    }
}
